import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getSuggestedVerses } from '../utils/verseAlgorithm';
import { GROWTH_AREA_COLORS } from '../utils/constants';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const Suggestions = ({ 
  discipleId, 
  allVerses, 
  userAppID, 
  notes, 
  historicalVerses,
  sentVerses = [],
  onSuggestionsGenerated 
}) => {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExplanations, setShowExplanations] = useState(true);
  const [missionalScale, setMissionalScale] = useState(-5);
  const [mentorDetails, setMentorDetails] = useState(null);

  // Memoize the input data to prevent unnecessary regeneration
  const memoizedData = useMemo(() => ({
    notes: notes || [],
    allVerses: allVerses || [],
    historicalVerses: historicalVerses || []
  }), [notes, allVerses, historicalVerses]);

  const generateSuggestions = useCallback(async () => {
    if (!allVerses?.length) return;
    
    try {
      setLoading(true);
      
      // Debug logs for incoming data
      console.log('=== SUGGESTIONS DEBUG ===');
      console.log('1. Notes count:', notes?.length);
      console.log('2. userAppID:', userAppID);
      console.log('3. discipleId:', discipleId);
      console.log('4. Fetched missional scale:', missionalScale);
      
      // Create Set of sent verse references for O(1) lookup
      const sentVerseRefs = new Set(sentVerses.map(v => v.verseReference));
      
      // Get available verses (not yet sent)
      const availableVerses = (allVerses || []).filter(v => !sentVerseRefs.has(v.reference));

      // Get suggestions using the new algorithm
      // TODO: Replace the following context object with the actual context from FaithJourneyPage/mentorDetails
      const context = {
        ageRange: mentorDetails?.faithJourneyAgeRange,
        relationshipStatus: mentorDetails?.faithJourneyRelationship,
        discipleshipStage: mentorDetails?.faithJourneyStage,
        christianExperience: mentorDetails?.faithJourneyExperience
      };
      const generatedSuggestions = getSuggestedVerses({
        context,
        notes: notes || [],
        availableVerses,
        count: 4
      });
      setSuggestions(generatedSuggestions || []);
      if (onSuggestionsGenerated && generatedSuggestions?.length > 0) {
        onSuggestionsGenerated(generatedSuggestions);
      }
    } catch (error) {
      console.error('Error generating suggestions:', error);
    } finally {
      setLoading(false);
    }
  }, [allVerses, notes, historicalVerses, sentVerses, onSuggestionsGenerated, missionalScale, userAppID, discipleId]);

  // Effect to fetch missional scale
  useEffect(() => {
    const fetchMissionalScale = async () => {
      if (!discipleId || !userAppID) {
        console.log('Suggestions: Missing discipleId or userAppID for scale fetch');
        return;
      }
      try {
        const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
        const docSnap = await getDoc(mentorDetailsRef);
        if (docSnap.exists()) {
          setMissionalScale(docSnap.data().missionalScale || -5); // Default if field missing
          setMentorDetails(docSnap.data());
          console.log('Suggestions: Fetched missional scale:', docSnap.data().missionalScale);
        } else {
          setMissionalScale(-5); // Default if doc missing
          console.log('Suggestions: Mentor details not found, using default scale.');
        }
      } catch (error) {
        console.error("Error fetching missional scale in Suggestions:", error);
        setMissionalScale(-5); // Default on error
      }
    };

    fetchMissionalScale();
  }, [discipleId, userAppID]);

  useEffect(() => {
    if (discipleId && userAppID && allVerses?.length) {
      generateSuggestions();
    }
  }, [discipleId, userAppID, allVerses, notes, generateSuggestions, missionalScale]);

  if (loading) {
    return <div className="animate-pulse p-4 bg-gray-50 rounded-md">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
      </div>
    </div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-medium text-gray-500">
          {suggestions.length === 0 
            ? 'No suggestions available' 
            : `${suggestions.length} verses suggested`}
        </h3>
        <button
          onClick={() => setShowExplanations(!showExplanations)}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          {showExplanations ? 'Hide Explanations' : 'Show Why Suggested'}
        </button>
      </div>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        {suggestions.map((suggestion, index) => (
          <div 
            key={`${suggestion.verse.id}-${index}`}
            className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-bold">{suggestion.verse.reference}</h4>
              {suggestion.verse.difficultyLevel && (
                <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full">
                  Level {suggestion.verse.difficultyLevel}
                </span>
              )}
            </div>

            <p className="text-sm text-gray-600 mb-3">{suggestion.verse.text}</p>

            {showExplanations && (
              <div className="mb-3 bg-gray-50 p-3 rounded-md">
                <p className="text-xs text-gray-600">{suggestion.explanation}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-3">
              {suggestion.verse.growthArea && (
                <span className={`text-xs px-2 py-1 ${
                  GROWTH_AREA_COLORS[suggestion.verse.growthArea]?.tailwindBg || 'bg-gray-100'
                } ${
                  GROWTH_AREA_COLORS[suggestion.verse.growthArea]?.tailwindText || 'text-gray-700'
                } rounded-full`}>
                  {suggestion.verse.growthArea}
                </span>
              )}
              {suggestion.verse.devTopic && (
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                  {suggestion.verse.devTopic}
                </span>
              )}
            </div>

            <button
              onClick={() => {
                navigate(`/send-message`, {
                  state: {
                    verse: suggestion.verse,
                    discipleId: discipleId,
                    userAppID: userAppID
                  }
                });
              }}
              className="w-fit bg-blue-500 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-600 transition-colors"
            >
              Build Message
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Suggestions;
